import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ChevronDown } from 'lucide-react';
import { Badge, Button, Loading, Modal, CopyableId } from '@/kit';
import { platformApi } from '../../../api/endpoints/platform';
import { handleApiError } from '../../../api/errorMapping';
import { Banner } from '../../../components/ui/Banner';
import { formatDateMx } from '../../../lib/dateHelpers';
import type { PlatformTenantStatusLocal } from '../../../api/types/PlatformLocal';

const STATUS_META: Record<
    PlatformTenantStatusLocal,
    { label: string; variant: 'default' | 'success' | 'warning' | 'danger' | 'info' }
> = {
    provisioning: { label: 'En provisión', variant: 'info' },
    active: { label: 'Activo', variant: 'success' },
    suspended: { label: 'Suspendido', variant: 'danger' },
    // DEC-RBAC-058: tenant creado pero sin las tablas DDB del silo → no operativo. Badge de advertencia.
    'pending-infra': { label: 'Pendiente de infra', variant: 'warning' },
};

interface Props {
    tenantId: string;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

/** Valor corto en grid: label arriba (chico), valor abajo. */
function Field({ label, children }: { label: string; children: ReactNode }) {
    return (
        <div className="space-y-1">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {label}
            </p>
            <div className="break-words text-sm font-medium">{children}</div>
        </div>
    );
}

/**
 * Sección con título de bloque y los valores en un grid de 2 columnas. Encerrada en una tarjeta sutil
 * (borde + fondo) para dar jerarquía visual sin saturar.
 */
function Section({ title, children }: { title: string; children: ReactNode }) {
    return (
        <section className="rounded-lg border bg-card p-4">
            <h3 className="mb-3 text-sm font-semibold text-foreground">{title}</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
        </section>
    );
}

/**
 * Vista read-only del detalle de un tenant. Trae el detalle completo con getTenant (la fila de la
 * lista sólo tiene un subset) y lo muestra en secciones (Identidad, Pool, Seguridad, Branding,
 * Auditoría). El ruido técnico de infra (IAM Role ARN, bucket S3, connector ARNs) y los IDs de
 * plomería (Pool ARN, App Client ID, prefijo de tablas, User Pool ID) viven detrás de un colapsable
 * "Detalles técnicos" — no en primer plano. El tipo de tenant se enriquece con su displayName + niveles
 * (GET /platform/tenant-types). Sin acciones — editar/suspender viven en la tabla.
 *
 * Usa el <Modal> del kit (Radix Dialog, header con título + Cerrar). DEC-060: emailDomain fuera del modelo.
 */
export function TenantDetail({ tenantId, open, onOpenChange }: Props) {
    const [showTech, setShowTech] = useState(false);
    const queryClient = useQueryClient();

    const { data, isLoading } = useQuery({
        queryKey: ['platform', 'tenants', tenantId],
        queryFn: () => platformApi.getTenant(tenantId),
        enabled: open,
    });

    // DEC-RBAC-059: re-verifica la infra del silo. Si ya están todas las tablas, el backend siembra los
    // roles diferidos y deja el tenant `active`; invalidamos el detalle (y el listado) para que la sección
    // "Estado de infraestructura" desaparezca y el badge pase a Activo. Si aún faltan, el detalle refrescado
    // trae la lista actualizada.
    const verifyMut = useMutation({
        mutationFn: () => platformApi.verifyTenantInfra(tenantId),
        onSuccess: async (res) => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ['platform', 'tenants', tenantId] }),
                queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] }),
            ]);
            if (res.status === 'active') {
                toast.success('Infraestructura lista. Tenant activado.');
            } else {
                toast.info('Todavía faltan tablas. Revisa la lista actualizada.');
            }
        },
        onError: handleApiError,
    });

    // Enriquecemos el tipo (ej. `retail` → "Retail" + niveles). Cae con gracia al raw si el catálogo
    // de tipos no resuelve. Se cachea por separado y se comparte con el resto del módulo Platform.
    const { data: tenantTypes } = useQuery({
        queryKey: ['platform', 'tenant-types'],
        queryFn: () => platformApi.getTenantTypes(),
        enabled: open,
    });

    if (!open) return null;

    const typeInfo = data?.tenantType
        ? tenantTypes?.items.find((t) => t.tenantType === data.tenantType)
        : undefined;

    const connectorArns = data?.connectorArns ? Object.entries(data.connectorArns) : [];
    const hasInfra = !!(data?.iamRoleArn || data?.s3Bucket || connectorArns.length > 0);

    const status = data ? STATUS_META[data.status] : null;

    return (
        <Modal
            isOpen={open}
            onClose={() => {
                onOpenChange(false);
            }}
            size="lg"
            title={data?.displayName ?? tenantId}
            titleAction={
                status && data ? (
                    <div className="flex flex-wrap items-center gap-2">
                        <Badge variant={status.variant}>{status.label}</Badge>
                        {(typeInfo?.displayName ?? data.tenantType) && (
                            <Badge variant="default">
                                {typeInfo?.displayName ?? data.tenantType}
                            </Badge>
                        )}
                    </div>
                ) : undefined
            }
        >
            {isLoading || !data ? (
                <div className="flex justify-center py-10">
                    <Loading />
                </div>
            ) : (
                <div className="space-y-4">
                    {/* Resumen de cabecera: id copiable de un vistazo. */}
                    <CopyableId label="Tenant ID" value={data.tenantId} />

                    {/* DEC-RBAC-058: sección de infra SOLO cuando el tenant está `pending-infra` (creado
                        pero sin las tablas del silo → no operativo). Lista las tablas faltantes + botón de
                        re-verificación. Al volver `active`, la query se invalida y la sección desaparece. */}
                    {data.status === 'pending-infra' && (
                        <section className="rounded-lg border border-warning/40 bg-warning/10 p-4">
                            <h3 className="mb-3 text-sm font-semibold text-foreground">
                                Estado de infraestructura
                            </h3>
                            <Banner variant="warning" className="mb-3">
                                El tenant está pendiente de infraestructura. Contacta al equipo de
                                infraestructura para que cree estos recursos.
                            </Banner>
                            <p className="mb-1.5 text-sm font-medium">Tablas faltantes</p>
                            {data.infraRequirements && data.infraRequirements.length > 0 ? (
                                <ul className="space-y-1 rounded-md border bg-muted p-3">
                                    {[...data.infraRequirements]
                                        .sort((a, b) => a.localeCompare(b, 'es'))
                                        .map((t) => (
                                            <li key={t}>
                                                <code className="select-all break-all font-mono text-sm text-foreground">
                                                    {t}
                                                </code>
                                            </li>
                                        ))}
                                </ul>
                            ) : (
                                <p className="text-sm text-muted-foreground">
                                    No hay tablas pendientes informadas.
                                </p>
                            )}
                            <div className="mt-4 flex justify-end">
                                <Button
                                    isLoading={verifyMut.isPending}
                                    onClick={() => {
                                        verifyMut.mutate();
                                    }}
                                >
                                    Re-verificar infraestructura
                                </Button>
                            </div>
                        </section>
                    )}

                    <Section title="Identidad">
                        <Field label="Nombre">{data.displayName}</Field>
                        <Field label="Tipo">
                            {typeInfo ? (
                                <div className="space-y-1">
                                    <span>{typeInfo.displayName}</span>
                                    {typeInfo.levels.length > 0 && (
                                        <div className="flex flex-wrap gap-1">
                                            {typeInfo.levels.map((lvl) => (
                                                <Badge key={lvl} variant="info">
                                                    {lvl}
                                                </Badge>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                (data.tenantType ?? '—')
                            )}
                        </Field>
                        <Field label="Estado">
                            <Badge variant={status?.variant ?? 'default'}>
                                {status?.label ?? data.status}
                            </Badge>
                        </Field>
                        <Field label="Scope">{data.scope ?? '—'}</Field>
                    </Section>

                    <Section title="Pool Cognito">
                        <Field label="Región">{data.cognitoPool.region}</Field>
                        <Field label="Nombre del pool">{data.cognitoPool.displayName}</Field>
                    </Section>

                    {data.securityPolicy && (
                        // DEC-RBAC-051: `mfaRequired`/`sessionTimeoutMin` purgados (write-only sin
                        // enforcement). DEC-RBAC-052: `passwordPolicy` ya no es per-tenant (política
                        // global Cognito, GET /public/password-policy). Solo quedan los campos vivos.
                        <Section title="Seguridad">
                            <Field label="2FA">
                                {(data.securityPolicy.mfaEnabled ?? true)
                                    ? 'Activado'
                                    : 'Desactivado'}
                            </Field>
                            {/* Con 2FA apagado los métodos no aplican → no se muestran. */}
                            {(data.securityPolicy.mfaEnabled ?? true) && (
                                <Field label="Métodos">
                                    {data.securityPolicy.methodsAllowed?.join(', ') ?? '—'}
                                </Field>
                            )}
                        </Section>
                    )}

                    {data.brandingConfig && (
                        <Section title="Branding">
                            <Field label="Color primario">
                                {data.brandingConfig.primaryColor ? (
                                    <span className="inline-flex items-center gap-2">
                                        <span
                                            className="inline-block size-4 rounded border"
                                            style={{
                                                backgroundColor: data.brandingConfig.primaryColor,
                                            }}
                                        />
                                        <code className="font-mono text-xs">
                                            {data.brandingConfig.primaryColor}
                                        </code>
                                    </span>
                                ) : (
                                    '—'
                                )}
                            </Field>
                            <Field label="Logo">
                                {data.brandingConfig.logoUrl ? (
                                    <a
                                        href={data.brandingConfig.logoUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-brand-accent underline underline-offset-2"
                                    >
                                        Ver logo
                                    </a>
                                ) : (
                                    '—'
                                )}
                            </Field>
                        </Section>
                    )}

                    <Section title="Auditoría">
                        <Field label="Creado">
                            {data.createdAt ? formatDateMx(data.createdAt) : '—'}
                        </Field>
                        <Field label="Actualizado">
                            {data.updatedAt ? formatDateMx(data.updatedAt) : '—'}
                        </Field>
                        <Field label="Creado por">{data.createdBy ?? '—'}</Field>
                        <Field label="Actualizado por">{data.updatedBy ?? '—'}</Field>
                    </Section>

                    {/* Detalles técnicos / plomería de infra: colapsado por defecto, fuera del primer plano. */}
                    <div className="rounded-lg border bg-muted/30">
                        <button
                            type="button"
                            aria-expanded={showTech}
                            className="flex w-full items-center justify-between px-4 py-3 text-sm font-semibold text-muted-foreground hover:text-foreground"
                            onClick={() => {
                                setShowTech((v) => !v);
                            }}
                        >
                            Detalles técnicos
                            <ChevronDown
                                className={`size-4 transition-transform ${showTech ? 'rotate-180' : ''}`}
                            />
                        </button>
                        {showTech && (
                            <div className="space-y-3 border-t px-4 py-4">
                                <CopyableId label="Prefijo de tablas" value={data.tablePrefix} />
                                <CopyableId
                                    label="User Pool ID"
                                    value={data.cognitoPool.userPoolId}
                                />
                                <CopyableId
                                    label="App Client ID"
                                    value={data.cognitoPool.appClientId}
                                />
                                <CopyableId label="Pool ARN" value={data.cognitoPool.userPoolArn} />
                                {data.iamRoleArn && (
                                    <CopyableId label="IAM Role ARN" value={data.iamRoleArn} />
                                )}
                                {data.s3Bucket && (
                                    <CopyableId label="Bucket S3" value={data.s3Bucket} />
                                )}
                                {connectorArns.map(([name, arn]) => (
                                    <CopyableId
                                        key={name}
                                        label={`Connector · ${name}`}
                                        value={arn}
                                    />
                                ))}
                                {!hasInfra && (
                                    <p className="text-xs text-muted-foreground">
                                        Sin recursos de infraestructura asociados.
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </Modal>
    );
}
